import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import TimeAgo from './TimeAgo';
import LikeComment from './LikeComment';
import Comments from './Comments';
import secureLocalStorage from 'react-secure-storage';
import renderHTML from "./RenderHtml";
import linkifyHashtags from "./linkifyHashtags";
import UserAvatar from './UserAvatar';
import AudioPlayer from './AudioPlayer';
import PdfViewer from './PdfViewer';
import SharedPostBox from './SharedPostBox';
import { useUserContext } from '../context/UserContext';

const PostDocCard = ({ src, title, file, onClick }) => {
    const [pdfPages, setPdfPages] = useState(0);
    const docTitle = title && title !== 'posted an update' ? title : null;
    return (
        <div className="post-doc-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className="post-doc-card-header">
                <i className="fas fa-file-alt post-doc-card-file-icon" />
                <div className="post-doc-card-meta">
                    {docTitle && <span className="post-doc-card-title">{docTitle}</span>}
                    {pdfPages > 0 && <span className="post-doc-card-pages">{pdfPages} {pdfPages === 1 ? 'page' : 'pages'}</span>}
                </div>
                <a
                    href={`https://buzzinguniverse.com/backend/${file}`}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="post-doc-card-download"
                    onClick={e => e.stopPropagation()}
                    title="Download PDF"
                >
                    <i className="fas fa-download" />
                </a>
            </div>
            <PdfViewer src={src} onLoad={setPdfPages} />
        </div>
    );
};

const getPostTextStyle = (details) => {
    if (!details || details === 'null') return {};
    const text = details.replace(/<[^>]*>/g, '').trim();
    const len = text.length;
    if (len <= 40)  return { fontSize: '30px', fontWeight: '600', lineHeight: '1.3' };
    if (len <= 100) return { fontSize: '22px', fontWeight: '500', lineHeight: '1.4' };
    if (len <= 200) return { fontSize: '18px', lineHeight: '1.5' };
    return { fontSize: '15px', lineHeight: '1.6' };
};

const formatDateTime = (date) =>
    new Date(date).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

const PRIVACY_META = {
    0: { icon: 'fa-globe',       label: 'Public'       },
    1: { icon: 'fa-lock',        label: 'Only Me'      },
    2: { icon: 'fa-user-friends', label: 'Friends Only' },
};

// Full post with likes and comments, opened from a comment activity without leaving the page
const PostPopup = ({ postId, title, onClose }) => {
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));
    const [popupPost, setPopupPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const fetchPost = async () => {
        try {
            const res = await axios.get(`https://buzzinguniverse.com/backend/api/get-sinlge-post-request?post_id=${postId}&visitor_id=${auth?.id || 0}`);
            if (res.data.code === 200) setPopupPost(res.data.data);
            else setNotFound(true);
        } catch (error) {
            console.error('Failed to load post:', error);
            setNotFound(true);
        }
    };

    useEffect(() => {
        fetchPost();
        document.body.classList.add('hidden');
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.classList.remove('hidden');
            window.removeEventListener('keydown', onKey);
        };
    }, [postId]);

    return createPortal(
        <div className="page-actions-backdrop" onClick={onClose}>
            <div className="page-actions-modal page-actions-modal-wide post-popup" onClick={(e) => e.stopPropagation()}>
                <div className="page-actions-modal-head">
                    <h5>{title}</h5>
                    <span className="page-actions-close" onClick={onClose}>&times;</span>
                </div>
                <div className="personal_des_box_main">
                    {notFound && <div className="page-actions-empty">This post is no longer available.</div>}
                    {!notFound && !popupPost && <div className="page-actions-empty"><div className="spinner-border text-primary" role="status" /></div>}
                    {popupPost && (popupPost.shared
                        ? <SharedPostBox post={popupPost} usrAuth={popupPost.post_data.user.id === auth?.id} />
                        : <PostBox1 post={popupPost} usrAuth={popupPost.post_data.user.id === auth?.id} setSinlgePost={() => {}} inGroup={false} groupAdmins={[]} noSkeleton GetPoost={fetchPost} />)}
                </div>
            </div>
        </div>,
        document.body
    );
};

const PostBox1 = ({ post, usrAuth, setSinlgePost, inGroup, groupAdmins, noSkeleton = false, GetPoost }) => {
    const [loading, setLoading] = useState(!noSkeleton);
    const [showCommentedPost, setShowCommentedPost] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [privacy, setPrivacy] = useState(post.post_data.privacy ?? 0);
    const [postDate, setPostDate] = useState(post.post_data.created_at);
    const [isDateEdited, setIsDateEdited] = useState(post.post_data.is_date_edited === true);
    const [taggedUsers, setTaggedUsers] = useState(post.tagged_users || []);
    const didInitialLoad = useRef(false);
    const postCardRef = useRef(null);

    const navigate = useNavigate();
    const { activeProfile } = useUserContext();

    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));

    const handleRemoveTag = async (userId) => {
        try {
            const res = await axios.post('https://buzzinguniverse.com/backend/api/remove-tag', {
                post_id: post.post_data.id,
                user_id: userId,
            });
            if (res.data.code === 200) {
                setTaggedUsers(prev => prev.filter(u => u.id !== userId));
                setShowPopup(false);
            }
        } catch (error) {
            console.error('Remove tag failed:', error);
        }
    };

    const chekStrg = (strg) => {
        if (strg == "https://buzzinguniverse.com/backend/") {
            return null;
        } else {
            return strg
        }
    }

    useEffect(() => {
        if (didInitialLoad.current) return;
        didInitialLoad.current = true;
        if (post) {
            setTimeout(() => setLoading(false), 2000);
        }
    }, [post]);

    useEffect(() => {
        const container = postCardRef.current;
        if (!container) return;

        const handleClick = (e) => {
            const mention = e.target.closest('[nav_id]');
            if (mention) {
                const type = mention.getAttribute('mention_type');
                const id = mention.getAttribute('nav_id');
                if (type === 'group' || type === 'company') {
                    const name = mention.getAttribute('nav_name') || 'page';
                    navigate(`/company/${id}/${name}`);
                } else {
                    navigate(`/member/${id}/profile`);
                }
                return;
            }
            const hashtag = e.target.closest('a.post-hashtag');
            if (hashtag) {
                e.preventDefault();
                navigate(`/hashtag/${hashtag.getAttribute('data-tag')}`);
            }
        };

        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [navigate])

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    if (loading) {
        return (
            <div className="skeleton">
                <div className="header">
                    <div className="profile-pic"></div>
                    <div>
                        <div className="text"></div>
                        <div className="timestamp"></div>
                    </div>
                </div>
                <div className="image"></div>
            </div>
        );
    }

    const group = post.group_data || null;

    // Show the group/company as the post author only when the post was made as the group/company
    // (as_group flag). A post a member/admin made as themselves keeps their own name everywhere,
    // with "in <group>" on the activity feed.
    const isAdmin = group && post.post_data.as_group == 1;

    // The dot belongs to the profile the viewer is on right now, not to every post they wrote
    const showOnlineDot = isAdmin
        ? String(activeProfile?.id) === String(group.id)
        : !activeProfile && auth?.id == post.post_data.user.id;

    // Comment activity: say which post was commented on. Non-public posts open only for those involved.
    const commentedOn = post.post_data.commented_on;
    const commentedOnLabel = commentedOn && (
        commentedOn.user_id == post.post_data.user_id && !commentedOn.group_id && post.post_data.as_group != 1
            ? 'their own post'
            : `${commentedOn.name}'s post`
    );
    const commentedOnLink = commentedOn
        && (commentedOn.is_public || auth?.id == commentedOn.user_id || auth?.id == post.post_data.user_id)
        ? `/member/${commentedOn.user_id}/profile/post/${commentedOn.post_id}`
        : null;
    const openCommentedPost = (e) => {
        e.preventDefault();
        setShowCommentedPost(true);
    };

    return (
        <div className="personal_des_box activity-all-memeber-boxs" ref={postCardRef}>
            <ul className='personal_des_list'>
                <li data-user-online={showOnlineDot}>
                    {isAdmin ? (
                        <Link to={`/company/${group.id}/${encodeURIComponent(group.name)}`}>
                            <UserAvatar
                                src={chekStrg(group.profile_picture) ? `https://buzzinguniverse.com/backend/${group.profile_picture}` : null}
                                alt="img"
                                isCompany={true}
                                isGroup={group.type !== 'company'}
                            />
                        </Link>
                    ) : (
                        <Link to={`/member/${post.post_data.user.id}/profile`}>
                            <UserAvatar
                                src={chekStrg(post.post_data.user?.photo) ? `https://buzzinguniverse.com/backend/${post.post_data.user.photo}` : '/images/default.png'}
                                alt="img"
                            />
                        </Link>
                    )}
                </li>
                <li>
                    {isAdmin ? (
                        <Link to={`/company/${group.id}/${encodeURIComponent(group.name)}`}>{group.name}</Link>
                    ) : (
                        <Link to={`/member/${post.post_data.user.id}/profile`}>{post.post_data.user.first_name} {post.post_data.user.last_name}</Link>
                    )}
                    {taggedUsers.length > 0 ? (
                        <div className="tf-tagged-users">
                            is with
                            {taggedUsers.slice(0, 2).map((u, index) => (
                                <span key={u.id} className="tf-tag-item ms-1">
                                    <Link to={`/member/${u.id}/profile`} className="tf-tag-badge">
                                        {u.first_name} {u.last_name}
                                    </Link>
                                    {index < Math.min(1, taggedUsers.length - 1) && ','}
                                </span>
                            ))}
                            {taggedUsers.length > 2 && (
                                <span className="tf-tag-badge ms-1 cursor-pointer" onClick={() => setShowPopup(true)}>
                                    and {taggedUsers.length - 2} others
                                </span>
                            )}
                        </div>
                    ) : (!inGroup && <p className={commentedOn ? 'commented-on-title' : undefined}>{commentedOn
                        ? <>commented on {commentedOnLink ? <Link to={commentedOnLink} onClick={openCommentedPost}>{commentedOnLabel}</Link> : commentedOnLabel}</>
                        : ['pdf','doc','docx','ppt','pptx','xls','xlsx'].includes(post.post_data.file_type) ? 'posted an update' : post.post_data.title}</p>)}

                </li>
                {group && !inGroup && !isAdmin && <li>
                    <Link className='groupNbgh' to={`/company/${group.id}/${encodeURIComponent(group.name)}`}>
                        in
                        <UserAvatar
                            src={chekStrg(group.profile_picture)
                                ? `https://buzzinguniverse.com/backend/${group.profile_picture}`
                                : (group.type === 'company' ? null : '/images/mystery-group.png')}
                            alt="img"
                            isCompany={group.type === 'company'}
                        />
                        {group.name}
                    </Link>
                </li>}
                <li>
                    <strong>{formatDateTime(postDate)}</strong>
                    {isDateEdited && <span className="post-edited-badge">edited</span>}
                    <span className="post-privacy-badge">
                        <i className={`fas ${PRIVACY_META[privacy]?.icon}`}></i>
                        {PRIVACY_META[privacy]?.label}
                    </span>
                </li>
            </ul>
            {post.post_data.type == 3 ? (() => {
                const usrt = JSON.parse(post.post_data.profile_data);
                // Live user data first — the snapshot is taken at signup, before a location is added
                const liveUser = post.user_data || usrt || {};
                const location = [liveUser.city, liveUser.country].filter((v) => v && v !== 'null' && v !== 'undefined' && String(v).trim()).join(', ');
                return usrt && <div className="search-groups-boxs">
                    <div className="search-groups-box-back" style={{ backgroundImage: `url(${chekStrg(usrt.cover_photo) ? `https://buzzinguniverse.com/backend/${usrt.cover_photo}` : ''})` }} />
                    <ul className="profile_upd">
                        <li>
                            <span><UserAvatar src={chekStrg(usrt.photo) ? `https://buzzinguniverse.com/backend/${usrt.photo}` : '/images/default.png'} alt="img" /></span>
                        </li>
                        <li style={{ position: 'relative' }}>
                            <h5><Link to={`/member/${usrt.id}/profile`}>{usrt.first_name} {usrt.last_name}</Link></h5>
                            {/* Absolute so the name keeps its original spot; the card's bottom padding gives it room */}
                            {location && <p className="profile_upd_location" style={{ position: 'absolute', top: '100%', left: 0, width: 'max-content', maxWidth: 'min(480px, 60vw)', margin: '4px 0 0', fontSize: '14px', lineHeight: 1.4, color: '#6b6b6b', textAlign: 'left', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }}>
                                <i className="fas fa-map-marker-alt" style={{ marginRight: '6px' }}></i>{location}
                            </p>}
                        </li>
                    </ul>
                </div>
            })()
                : <>{post.post_data.details != 'null' && <p className="post-text-dynamic" style={getPostTextStyle(post.post_data.details)}>{renderHTML(linkifyHashtags(post.post_data.details))}</p>}
                    {commentedOnLink && (
                        <button type="button" className="view-post-btn" onClick={openCommentedPost}>
                            View post <i className="fas fa-arrow-right" />
                        </button>
                    )}
                    {/* Popup opens only from the media itself, not the empty space beside it */}
                    <div>
                        {post.post_data.file_type === 'jpg' || post.post_data.file_type === 'png' || post.post_data.file_type === 'gif' ? (
                            <div className="image-container">
                                {!imageLoaded && <div className="skeleton">
                                    <div className="image"></div>
                                </div>}
                                <img
                                    src={`https://buzzinguniverse.com/backend/${post.post_data.file}`}
                                    alt="img"
                                    className="personal_des_box_image"
                                    onLoad={handleImageLoad}
                                    onClick={() => setSinlgePost(post)}
                                    style={{ display: imageLoaded ? 'block' : 'none', cursor: 'pointer' }}
                                />
                            </div>
                        ) : null}
                        {post.post_data.file_type === 'mp4' || post.post_data.file_type === 'mov' || post.post_data.file_type === 'webm' ? (
                            <div className="image-container" onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                                {!imageLoaded && <div className="skeleton">
                                    <div className="image"></div>
                                </div>}
                                <video
                                    src={`https://buzzinguniverse.com/backend/${post.post_data.file}`}
                                    className="personal_des_box_image"
                                    onLoadedData={handleImageLoad}
                                    style={{ display: imageLoaded ? 'block' : 'none' }}
                                    controls
                                ></video>
                            </div>
                        ) : null}
                        {post.post_data.file_type === 'mp3' || post.post_data.file_type === 'wav' || post.post_data.file_type === 'ogg' ? (
                            <div onClick={(e) => e.stopPropagation()}>
                                <AudioPlayer
                                    src={`https://buzzinguniverse.com/backend/${post.post_data.file}`}
                                    title={post.post_data.file?.split('/').pop().replace(/\.[^.]+$/, '').replace(/^\d+_/, '').replace(/[-_]/g, ' ')}
                                    onLoad={handleImageLoad}
                                />
                            </div>
                        ) : null}
                        {post.post_data.file_type === 'pdf' ? (
                            <PostDocCard
                                src={`https://buzzinguniverse.com/backend/${post.post_data.file}`}
                                title={post.post_data.title}
                                file={post.post_data.file}
                                onClick={() => setSinlgePost(post)}
                            />
                        ) : null}
                        {['doc','docx','ppt','pptx','xls','xlsx'].includes(post.post_data.file_type) ? (
                            <div className="post-doc-card" onClick={() => setSinlgePost(post)} style={{ cursor: 'pointer' }}>
                                <div className="post-doc-card-header">
                                    <i className={`fas ${post.post_data.file_type.startsWith('xls') ? 'fa-file-excel' : post.post_data.file_type.startsWith('ppt') ? 'fa-file-powerpoint' : 'fa-file-word'} post-doc-card-file-icon`} />
                                    <div className="post-doc-card-meta">
                                        <span className="post-doc-card-title">
                                            {post.post_data.file?.split('/').pop().replace(/^\d+_/, '').replace(/[-_]/g, ' ')}
                                        </span>
                                        <span className="post-doc-card-pages">{post.post_data.file_type.toUpperCase()}</span>
                                    </div>
                                    <a
                                        href={`https://buzzinguniverse.com/backend/${post.post_data.file}`}
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="post-doc-card-download"
                                        onClick={e => e.stopPropagation()}
                                        title="Download"
                                    >
                                        <i className="fas fa-download" />
                                    </a>
                                </div>
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent('https://buzzinguniverse.com/backend/' + post.post_data.file)}&embedded=true`}
                                    title="Document Preview"
                                    className="post-doc-iframe"
                                    style={{ width: '100%', height: '480px', border: 'none', borderRadius: '0 0 8px 8px' }}
                                />
                            </div>
                        ) : null}</div></>
            }
            {auth && <LikeComment post={post} GetPoost={GetPoost} onPrivacyChange={setPrivacy} onDateChange={(id, newDate) => { setPostDate(newDate); setIsDateEdited(true); }} isTagged={taggedUsers.some(u => u.id === auth?.id)} onRemoveTag={() => handleRemoveTag(auth?.id)} />}
            <div className='commentScroll1'>
                <Comments post={post} auth={auth} loadmore={true} GetPoost={GetPoost} />
            </div>
            {showCommentedPost && <PostPopup postId={commentedOn.post_id} title={`${commentedOn.name}'s post`} onClose={() => setShowCommentedPost(false)} />}
            {showPopup && (
                <div className="tf-popup-overlay" onClick={() => setShowPopup(false)}>
                    <div className="tf-popup" onClick={(e) => e.stopPropagation()}>
                        <h3 className="tf-title">Tagged Friends</h3>
                        <ul className="tf-user-list">
                            {taggedUsers.map((user) => {
                                return (
                                    <li key={user.id} className={`tf-user-item`}>
                                        <Link to={`/member/${user.id}/profile`} onClick={() => setShowPopup(false)} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', flex: 1 }}>
                                            <UserAvatar src={(user.photo && "https://buzzinguniverse.com/backend/" + user.photo) || "/images/default.png"} alt="profile" className='me-2' />
                                            <span>{user.first_name} {user.last_name}</span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                        <button className="tf-done-btn bg-danger" onClick={() => setShowPopup(false)}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PostBox1;
